import {ErrorHandler,inject,Injectable,PLATFORM_ID} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {isPlatformBrowser,DOCUMENT} from '@angular/common';
import {logEvent} from 'firebase/analytics';
import {FIREBASE_ANALYTICS} from './firebase/firebase.tokens';
import {ConsentService} from './consent/consent.service';
import {Router,NavigationError} from '@angular/router';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler{
	private readonly platformId=inject(PLATFORM_ID);
	private readonly resolveAnalytics=inject(FIREBASE_ANALYTICS);
	private readonly consent=inject(ConsentService);
	private readonly document=inject(DOCUMENT);
	private readonly router=inject(Router);

	constructor(){
		if(isPlatformBrowser(this.platformId)){
			this.router.events.pipe(takeUntilDestroyed()).subscribe((event)=>{
				if(event instanceof NavigationError){
					this.handleNavigationError(event);
				}
			});
		}
	}

	handleError(error:unknown):void{
		console.error(error);

		if(!isPlatformBrowser(this.platformId)){
			return;
		}

		if(this.isChunkLoadError(error)){
			this.handleChunkLoadError();
			return;
		}

		this.logErrorToAnalytics(error);
	}

	private handleNavigationError(event:NavigationError):void{
		if(this.isChunkLoadError(event.error)){
			this.handleChunkLoadError();
		}else{
			this.logErrorToAnalytics(event.error);
		}
	}

	private isChunkLoadError(error:unknown):boolean{
		if(error instanceof Error){
			return error.name==='ChunkLoadError'||
				error.message?.includes('Loading chunk')||
				error.message?.includes('Failed to fetch dynamically imported module');
		}
		return false;
	}

	private handleChunkLoadError():void{
		this.logErrorToAnalytics(new Error('ChunkLoadError — reloading application'));
		this.document.defaultView?.location.reload();
	}

	private logErrorToAnalytics(error:unknown):void{
		if(!this.consent.analytics()) return;
		const analytics=this.resolveAnalytics();
		if(!analytics) return;
		try{
			const errorMessage=error instanceof Error?error.message:String(error);
			const errorStack=error instanceof Error?error.stack?.substring(0,500):undefined;
			const url=this.document.defaultView?.location.href;
			logEvent(analytics,'app_error',{
				error_message:errorMessage.substring(0,100),
				error_stack:errorStack,
				url:url?.substring(0,200)
			});
		}catch{
			// Avoid infinite error loop if analytics logging itself fails
		}
	}
}
